import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { formatPhone } from "../me-ui";
import { Checkbox } from "@/components/ui/checkbox";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Settings → Phone (docs/16 §C, D31) — the inbound-routing config engine. Per
// number, an admin picks how inbound calls flow (owner / team / round-robin +
// business hours); twilio-inbound generates TwiML from routing_config. Voicemail
// + IVR fields are modeled here but marked Phase 3b (execution lands later).
// Ring targets are in-app softphone Client identities (= users.id) — no PSTN.
// Bespoke page (getSupabaseClient CRUD + shadcn), write-gated by manage_phone (RLS).
//
// NOTE: ring modes don't ring a browser end-to-end until the Phase-3a.5 inbound
// softphone receiver ships — a routed call falls through to the fallback message.

const MODES = [
  { value: "owner", label: "Ring owner", phase: "3a" },
  { value: "team", label: "Ring team (all at once)", phase: "3a" },
  { value: "round_robin", label: "Round-robin", phase: "3a" },
  { value: "voicemail_first", label: "Voicemail first", phase: "3b1" },
  { value: "ivr", label: "IVR menu", phase: "3b" },
] as const;
const DAYS = [
  { n: 1, label: "Mon" }, { n: 2, label: "Tue" }, { n: 3, label: "Wed" }, { n: 4, label: "Thu" },
  { n: 5, label: "Fri" }, { n: 6, label: "Sat" }, { n: 7, label: "Sun" },
];
const TZ_SUGGESTIONS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];

type User = { id: string; full_name: string | null };
type PhoneNumber = {
  id: string; e164: string; purpose: string | null;
  assigned_user_id: string | null; is_active: boolean; routing_config: RoutingConfig | null;
};
type BusinessHours = { tz: string; start: string; end: string; days: number[]; after_hours: "message" | "voicemail" };
type RoutingConfig = {
  mode: string; ring_seconds: number; team_user_ids: string[]; last_rr_index?: number;
  business_hours?: BusinessHours; fallback_message?: string;
  voicemail?: { greeting_text?: string; notify_user_id?: string };
  ivr?: { greeting_text?: string; options?: { digit: string; action: string; target?: string }[] };
};

const DEFAULT_BH: BusinessHours = { tz: "America/New_York", start: "08:00", end: "21:00", days: [1, 2, 3, 4, 5], after_hours: "message" };
const defaultConfig = (): RoutingConfig => ({
  mode: "owner", ring_seconds: 20, team_user_ids: [], last_rr_index: 0,
  business_hours: { ...DEFAULT_BH }, fallback_message: "",
});

// Buy-a-number (3b-3) — admin-only (hidden unless manage_phone=full; the
// twilio-numbers function enforces it regardless). Search → explicit confirm
// (real money) → buy; the new number wires its webhooks + seeds a phone_numbers row.
type Candidate = { phoneNumber: string; locality?: string; region?: string };
const BuyNumber = ({ onBought }: { onBought: () => void }) => {
  const notify = useNotify();
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [area, setArea] = useState("");
  const [contains, setContains] = useState("");
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);

  useEffect(() => {
    getSupabaseClient().rpc("capability_level", { p_cap: "manage_phone" }).then(({ data }) => setIsAdmin(data === "full"));
  }, []);
  if (!isAdmin) return null;

  const call = async (payload: Record<string, unknown>) => {
    const { data: sess } = await getSupabaseClient().auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio-numbers`, {
      method: "POST",
      headers: {
        apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY as string,
        Authorization: `Bearer ${sess.session?.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d?.error || `failed (${res.status})`);
    return d;
  };

  const search = async () => {
    setBusy(true); setConfirm(null);
    try {
      const d = await call({ action: "search", areaCode: area.trim() || undefined, contains: contains.trim() || undefined });
      setCandidates(d.numbers ?? []);
      if (!(d.numbers ?? []).length) notify("No matching numbers", { type: "info" });
    } catch (e) { notify(`Search failed: ${(e as Error).message}`, { type: "error" }); }
    finally { setBusy(false); }
  };

  const buy = async (phoneNumber: string) => {
    setBusy(true);
    try {
      const d = await call({ action: "buy", phoneNumber });
      notify(d.existed ? "Number already owned — added" : `Purchased ${phoneNumber}`, { type: "success" });
      setConfirm(null); setCandidates([]); setArea(""); setContains(""); onBought();
    } catch (e) { notify(`Purchase failed: ${(e as Error).message}`, { type: "error" }); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Buy a number</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)}>{open ? "Close" : "Search & buy"}</Button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-2">
          <div className="flex gap-2 items-end flex-wrap">
            <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Area code</span>
              <Input className="w-28" value={area} onChange={(e) => setArea(e.target.value)} placeholder="954" /></label>
            <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Contains</span>
              <Input className="w-36" value={contains} onChange={(e) => setContains(e.target.value)} placeholder="digits/letters" /></label>
            <Button size="sm" disabled={busy} onClick={search}>Search</Button>
          </div>
          {candidates.map((c) => (
            <div key={c.phoneNumber} className="flex items-center gap-2 text-sm border-b pb-2 flex-wrap">
              <span className="font-mono">{c.phoneNumber}</span>
              <span className="text-xs text-muted-foreground">{[c.locality, c.region].filter(Boolean).join(", ")}</span>
              {confirm === c.phoneNumber ? (
                <span className="ml-auto flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-amber-700">Purchases this number &amp; bills the Twilio account (~$1–2/mo).</span>
                  <Button size="sm" variant="destructive" disabled={busy} onClick={() => buy(c.phoneNumber)}>Confirm buy</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
                </span>
              ) : (
                <Button size="sm" variant="outline" className="ml-auto" disabled={busy} onClick={() => setConfirm(c.phoneNumber)}>Buy</Button>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
};

const PhonePage = () => {
  const notify = useNotify();
  const [numbers, setNumbers] = useState<PhoneNumber[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  // number meta draft
  const [purpose, setPurpose] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [active, setActive] = useState(true);
  // routing draft
  const [cfg, setCfg] = useState<RoutingConfig>(defaultConfig());
  const [hasConfig, setHasConfig] = useState(false);

  const load = async () => {
    const { data } = await getSupabaseClient().from("phone_numbers").select("*").order("e164");
    setNumbers((data as PhoneNumber[]) ?? []);
  };
  useEffect(() => {
    load();
    getSupabaseClient().from("users").select("id, full_name").eq("is_active", true).order("full_name")
      .then(({ data }) => setUsers((data as User[]) ?? []));
  }, []);

  const select = (n: PhoneNumber) => {
    setSelId(n.id);
    setPurpose(n.purpose ?? "");
    setOwnerId(n.assigned_user_id ?? "");
    setActive(n.is_active);
    setHasConfig(!!n.routing_config);
    setCfg(n.routing_config ? { ...defaultConfig(), ...n.routing_config } : defaultConfig());
  };

  const bh = cfg.business_hours ?? { ...DEFAULT_BH };
  const setBh = (patch: Partial<BusinessHours>) => setCfg((c) => ({ ...c, business_hours: { ...DEFAULT_BH, ...c.business_hours, ...patch } }));
  const toggleTeam = (id: string) =>
    setCfg((c) => ({ ...c, team_user_ids: c.team_user_ids.includes(id) ? c.team_user_ids.filter((x) => x !== id) : [...c.team_user_ids, id] }));
  const toggleDay = (n: number) =>
    setBh({ days: bh.days.includes(n) ? bh.days.filter((d) => d !== n) : [...bh.days, n].sort() });
  const setVm = (patch: Partial<NonNullable<RoutingConfig["voicemail"]>>) =>
    setCfg((c) => ({ ...c, voicemail: { ...c.voicemail, ...patch } }));

  const userName = (id: string | null) => users.find((u) => u.id === id)?.full_name ?? (id ? "(unknown)" : "—");

  const saveMeta = async () => {
    if (!selId) return;
    const { error } = await getSupabaseClient().from("phone_numbers")
      .update({ purpose: purpose.trim() || null, assigned_user_id: ownerId || null, is_active: active }).eq("id", selId);
    if (error) { notify(`Save failed: ${error.message}`, { type: "error" }); return; }
    notify("Number updated", { type: "success" }); load();
  };

  const saveRouting = async () => {
    if (!selId) return;
    // Persist only the fields relevant to the chosen mode + the always-present ones.
    // Voicemail is opt-in: keep the block only if it carries a greeting or a notify
    // target (+ when it's the active behavior). Empty → omit, so the number stays
    // default-safe (no voicemail).
    const vmGreeting = (cfg.voicemail?.greeting_text ?? "").trim();
    const vmNotify = cfg.voicemail?.notify_user_id || undefined;
    const wantsVm = cfg.mode === "voicemail_first" || cfg.business_hours?.after_hours === "voicemail";
    const voicemail = (vmGreeting || vmNotify || wantsVm)
      ? { greeting_text: vmGreeting || undefined, notify_user_id: vmNotify }
      : undefined;
    const payload: RoutingConfig = {
      ...cfg,
      ring_seconds: Number(cfg.ring_seconds) || 20,
      team_user_ids: cfg.team_user_ids,
      last_rr_index: cfg.last_rr_index ?? 0,
      fallback_message: (cfg.fallback_message ?? "").trim() || undefined,
      voicemail,
    };
    const { error } = await getSupabaseClient().from("phone_numbers").update({ routing_config: payload }).eq("id", selId);
    if (error) { notify(`Save failed: ${error.message}`, { type: "error" }); return; }
    notify("Routing saved", { type: "success" }); setHasConfig(true); load();
  };

  const clearRouting = async () => {
    if (!selId || !window.confirm("Clear routing? This number reverts to the default 'Please hold' behavior.")) return;
    const { error } = await getSupabaseClient().from("phone_numbers").update({ routing_config: null }).eq("id", selId);
    if (error) { notify(`Failed: ${error.message}`, { type: "error" }); return; }
    notify("Routing cleared — back to default", { type: "success" });
    setHasConfig(false); setCfg(defaultConfig()); load();
  };

  const modeLabel = (n: PhoneNumber) => (n.routing_config?.mode ? MODES.find((m) => m.value === n.routing_config?.mode)?.label ?? n.routing_config.mode : "default");
  const teamMode = cfg.mode === "team" || cfg.mode === "round_robin";
  const is3b = cfg.mode === "voicemail_first" || cfg.mode === "ivr";

  return (
    <div className="p-2 max-w-5xl">
      <h1 className="text-xl font-semibold">Phone settings</h1>
      <div className="text-sm text-muted-foreground mb-2">
        Manage numbers and per-number inbound-call routing. Ring targets are in-app softphones. A number with no
        routing keeps the default hold message.
      </div>
      <div className="mb-4 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 text-xs px-3 py-2">
        ⚠️ Ring modes route to the browser softphone, which does not yet receive inbound calls (Phase 3a.5). Until that
        ships, a routed call falls through to the fallback message — don't rely on ring modes end-to-end yet.
      </div>

      <BuyNumber onBought={load} />

      <div className="grid md:grid-cols-2 gap-4">
        {/* Numbers list */}
        <Card>
          <CardHeader><CardTitle className="text-base">Numbers</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {numbers.map((n) => (
              <div key={n.id} className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm border-b last:border-0 cursor-pointer hover:bg-muted/50 ${selId === n.id ? "bg-muted" : ""}`} onClick={() => select(n)}>
                <span className="font-mono">{formatPhone(n.e164)}</span>
                {n.purpose && <Badge variant="outline" className="text-[10px]">{n.purpose}</Badge>}
                <Badge variant="secondary" className="text-[10px]">{modeLabel(n)}</Badge>
                {!n.is_active && <Badge variant="outline" className="text-[10px] text-muted-foreground">inactive</Badge>}
                <span className="ml-auto text-xs text-muted-foreground">{userName(n.assigned_user_id)}</span>
              </div>
            ))}
            {numbers.length === 0 && <div className="text-sm text-muted-foreground py-2">No numbers on file. (Buying a number is Phase 3b.)</div>}
          </CardContent>
        </Card>

        {/* Editor */}
        <Card>
          <CardHeader><CardTitle className="text-base">{selId ? "Configure number" : "Select a number"}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!selId ? (
              <div className="text-sm text-muted-foreground">Pick a number on the left to set its owner and inbound routing.</div>
            ) : (
              <>
                {/* Number meta */}
                <div className="flex gap-3">
                  <label className="flex flex-col gap-1 flex-1">
                    <span className="text-xs text-muted-foreground">Purpose</span>
                    <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="dialer / acquisitions / …" />
                  </label>
                  <label className="flex flex-col gap-1 flex-1">
                    <span className="text-xs text-muted-foreground">Owner</span>
                    <select className="border rounded px-2 py-2 text-sm bg-background" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                      <option value="">(unassigned)</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{u.full_name ?? u.id}</option>)}
                    </select>
                  </label>
                </div>
                <label className="flex items-center gap-2">
                  <Switch checked={active} onCheckedChange={setActive} />
                  <span className="text-sm">Active</span>
                  <Button size="sm" variant="outline" className="ml-auto" onClick={saveMeta}>Save number</Button>
                </label>

                <div className="border-t pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Inbound routing</span>
                    {hasConfig
                      ? <Badge className="bg-green-100 text-green-800 text-[10px]">configured</Badge>
                      : <Badge variant="secondary" className="text-[10px]">default (Please hold)</Badge>}
                  </div>

                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground">Mode</span>
                    <select className="border rounded px-2 py-2 text-sm bg-background" value={cfg.mode} onChange={(e) => setCfg((c) => ({ ...c, mode: e.target.value }))}>
                      {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}{m.phase === "3b" ? " (Phase 3b)" : ""}</option>)}
                    </select>
                  </label>

                  {cfg.mode === "owner" && (
                    <div className="text-xs text-muted-foreground">Rings the number's owner: <b>{userName(ownerId || null)}</b>.</div>
                  )}

                  {teamMode && (
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-muted-foreground">Team {cfg.mode === "round_robin" ? "(rotated one at a time)" : "(all ring together)"}</span>
                      <div className="grid grid-cols-2 gap-1 max-h-40 overflow-auto rounded border p-2">
                        {users.map((u) => (
                          <label key={u.id} className="flex items-center gap-2 text-sm">
                            <Checkbox checked={cfg.team_user_ids.includes(u.id)} onCheckedChange={() => toggleTeam(u.id)} />
                            {u.full_name ?? u.id}
                          </label>
                        ))}
                        {users.length === 0 && <span className="text-xs text-muted-foreground">No users.</span>}
                      </div>
                    </div>
                  )}

                  {!is3b && (
                    <label className="flex flex-col gap-1 w-32">
                      <span className="text-xs text-muted-foreground">Ring seconds</span>
                      <Input type="number" min={5} max={120} value={cfg.ring_seconds} onChange={(e) => setCfg((c) => ({ ...c, ring_seconds: +e.target.value }))} />
                    </label>
                  )}

                  {/* Business hours */}
                  {!is3b && (
                    <div className="rounded border p-2 space-y-2">
                      <div className="text-xs font-medium">Business hours <span className="text-muted-foreground">(outside → after-hours action)</span></div>
                      <div className="flex gap-2 flex-wrap items-end">
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] text-muted-foreground">Timezone</span>
                          <Input list="tz-list" className="w-44" value={bh.tz} onChange={(e) => setBh({ tz: e.target.value })} />
                          <datalist id="tz-list">{TZ_SUGGESTIONS.map((t) => <option key={t} value={t} />)}</datalist>
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] text-muted-foreground">Start</span>
                          <Input type="time" className="w-28" value={bh.start} onChange={(e) => setBh({ start: e.target.value })} />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] text-muted-foreground">End</span>
                          <Input type="time" className="w-28" value={bh.end} onChange={(e) => setBh({ end: e.target.value })} />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] text-muted-foreground">After hours</span>
                          <select className="border rounded px-2 py-1.5 text-sm bg-background" value={bh.after_hours} onChange={(e) => setBh({ after_hours: e.target.value as BusinessHours["after_hours"] })}>
                            <option value="message">Play message</option>
                            <option value="voicemail">Voicemail</option>
                          </select>
                        </label>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {DAYS.map((d) => (
                          <label key={d.n} className="flex items-center gap-1 text-xs">
                            <Checkbox checked={bh.days.includes(d.n)} onCheckedChange={() => toggleDay(d.n)} />{d.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground">Fallback message <span className="opacity-60">(no answer / nothing applies)</span></span>
                    <Textarea rows={2} value={cfg.fallback_message ?? ""} onChange={(e) => setCfg((c) => ({ ...c, fallback_message: e.target.value }))} placeholder="Thank you for calling. Please hold." />
                  </label>

                  {/* Voicemail (Phase 3b-1 — active). Opt-in: filling a greeting or
                      picker adds a voicemail block; empty = no voicemail (default-safe). */}
                  <div className="rounded border p-2 space-y-2">
                    <div className="text-xs font-medium">Voicemail <span className="text-muted-foreground">(on no-answer, after-hours, or voicemail-first)</span></div>
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] text-muted-foreground">Greeting <span className="opacity-60">(blank → a default greeting)</span></span>
                      <Textarea rows={2} value={cfg.voicemail?.greeting_text ?? ""}
                        onChange={(e) => setVm({ greeting_text: e.target.value })}
                        placeholder="Sorry we missed your call. Please leave a message after the tone." />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] text-muted-foreground">Notify <span className="opacity-60">(who gets a task — defaults to the owner)</span></span>
                      <select className="border rounded px-2 py-2 text-sm bg-background" value={cfg.voicemail?.notify_user_id ?? ""}
                        onChange={(e) => setVm({ notify_user_id: e.target.value || undefined })}>
                        <option value="">Owner ({userName(ownerId || null)})</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.full_name ?? u.id}</option>)}
                      </select>
                    </label>
                  </div>

                  {/* IVR — modeled, Phase 3b-2 */}
                  <div className="rounded border border-dashed p-2 opacity-60 space-y-2">
                    <div className="text-xs font-medium flex items-center gap-2">IVR menu <Badge variant="outline" className="text-[10px]">Phase 3b-2</Badge></div>
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] text-muted-foreground">IVR greeting</span>
                      <Input disabled value={cfg.ivr?.greeting_text ?? ""} placeholder="Press 1 for… — executes in Phase 3b-2" />
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button onClick={saveRouting}>Save routing</Button>
                    {hasConfig && <Button variant="ghost" onClick={clearRouting}>Clear → default</Button>}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

PhonePage.path = "/settings/phone";
export default PhonePage;
